import React, { useState } from "react";
import {
  createTournament,
  updateTournament,
  deleteTournament,
} from "services/tournamentService";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";

const TournamentForm = ({
  existingTournament = null,
  onSuccess,
  locationId,
}) => {
  const [active, setActive] = useState(existingTournament?.active || true);
  const [name, setName] = useState(existingTournament?.name || "");
  const [registrationLimit, setRegistrationLimit] = useState(
    existingTournament?.waitingListLimit || 32
  );
  const [date, setDate] = useState(
    existingTournament?.date ? existingTournament.date.split("T")[0] : ""
  );
  const [time, setTime] = useState(
    existingTournament?.time || "19:00"
  );

  const [type, setType] = useState(existingTournament?.type || "DYP");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tournamentData = {
      active,
      name,
      waitingListLimit: registrationLimit,
      date: new Date(`${date}T00:00:00`).toISOString(),
      time,
      type,
      locationId,
      discardedAt: null,
      registrationClosed: false,
    };

    try {
      if (existingTournament) {
        await updateTournament(existingTournament.id, tournamentData);
      } else {
        await createTournament(tournamentData);
      }
      onSuccess();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error saving tournament:", error);
      setError("Une erreur est survenue lors de la sauvegarde.");
    }
  };

  const handleDelete = async () => {
    if (!existingTournament) return;

    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le tournoi « ${existingTournament.name} » ? Cette action est irréversible.`
    );

    if (!confirmed) return;

    try {
      await deleteTournament(existingTournament.id);
      onSuccess();
    } catch (error) {
      Sentry.captureException(error);
      alert(error.message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-slate-900 border border-slate-700 p-6 rounded-lg shadow-xl max-w-xl mx-auto text-slate-50 space-y-6"
    >
      <h2 className="text-2xl font-bold text-center">
        {existingTournament
          ? "Modifier le tournoi"
          : "Créer un nouveau tournoi"}
      </h2>

      {error && <p className="text-red-400 font-medium text-center">{error}</p>}

      <div className="flex items-center space-x-3">
        <label className="text-lg font-medium">Actif:</label>
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="form-checkbox h-5 w-5 text-green-500"
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">
          Nom du tournoi: (optionnel)
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-2 rounded bg-slate-800 border border-slate-700 focus:outline-none focus:ring focus:border-amber-400"
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Limite d'inscriptions:</label>
        <input
          type="number"
          value={registrationLimit}
          onChange={(e) => setRegistrationLimit(parseInt(e.target.value, 10))}
          min="1"
          className="w-full px-4 py-2 rounded bg-slate-800 border border-slate-700 focus:outline-none focus:ring focus:border-amber-400"
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Date du tournoi:</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
          className="w-full px-4 py-2 rounded bg-slate-800 border border-slate-700 focus:outline-none focus:ring focus:border-amber-400"
        />
      </div>
      <div>
        <label className="block mb-1 font-medium">Heure du tournoi:</label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          required
          className="w-full px-4 py-2 rounded bg-slate-800 border border-slate-700 focus:outline-none focus:ring focus:border-amber-400"
        />
      </div>

      <div>
        <label className="block mb-1 font-medium">Type de tournoi:</label>
        <div className="flex space-x-6">
          {["DYP", "Double Open", "Simple"].map((option) => (
            <label key={option} className="flex items-center space-x-2">
              <input
                type="radio"
                value={option}
                checked={type === option}
                onChange={() => setType(option)}
                className="form-radio text-amber-500"
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-between flex-wrap gap-4">
        <button
          type="submit"
          className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-md font-semibold transition"
        >
          {existingTournament ? "Mettre à jour" : "Créer"}
        </button>

        {existingTournament && (
          <button
            type="button"
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-md font-semibold transition"
          >
            Supprimer
          </button>
        )}

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="bg-slate-900 hover:bg-slate-900 text-white px-6 py-2 rounded-md font-semibold transition"
        >
          Annuler
        </button>
      </div>
    </form>
  );
};

export default TournamentForm;
